import axios from 'axios';
import PDFMerger from 'pdf-merger-js';

import { Filters } from '@/types/globalFilter';
import { TransmissionReportCount } from '@/types/transmissionReport';

import { TransmissionReportColorArray } from './constants';

/**
 * Utility function to merge PDFs from given URLs.
 * @param pdfUrls Array of URLs for the PDFs to be merged.
 * @returns A Uint8Array containing the merged PDF or null if no valid PDFs are added.
 */
export const mergePDFs = async (pdfUrls: string[]): Promise<Uint8Array | null> => {
  if (!pdfUrls || pdfUrls.length === 0) {
    console.warn('No PDF URLs provided for merging');
    return null;
  }

  const merger = new PDFMerger();

  for (const url of pdfUrls) {
    try {
      // Fetch PDF as an ArrayBuffer
      const response = await axios.get(url, { responseType: 'arraybuffer' });
      const pdfArrayBuffer = response.data;

      // Skip empty PDFs
      if (!pdfArrayBuffer || pdfArrayBuffer.byteLength === 0) {
        console.warn(`Skipping empty PDF fetched from URL: ${url}`);
        continue;
      }

      // Add PDF to the merger
      await merger.add(new Uint8Array(pdfArrayBuffer));
    } catch (error) {
      console.error(`Error fetching or adding PDF from URL: ${url}`, error);
    }
  }

  try {
    // Generate the merged PDF as a buffer
    const mergedPdfBuffer = await merger.saveAsBuffer();
    return new Uint8Array(mergedPdfBuffer);
  } catch (error) {
    console.error('Error generating merged PDF:', error);
    return null;
  }
};

export const fetchActiveFiltersCount = (filters: Filters | null | undefined) => {
  if (!filters) return 0;
  return Object.values(filters).filter((arr) => arr.length).length;
};

export const fetchIsAllUnsignedCountsZero = (counts: TransmissionReportCount) => {
  return TransmissionReportColorArray.every((color) => (counts?.[color.value] ?? 0) === 0);
};

export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
